import React, { useMemo, useState } from "react";
import { FiActivity, FiAlertCircle } from "react-icons/fi";
import { EmptyState } from "../../../common/comms/EmptyState";
import { useHormoneData } from "../core/api/useHormoneData";
import { buildCards } from "../core/model/cards";
import { RANGE_KEYS } from "../core/model/compare";
import { buildContext } from "../core/model/context";
import { shortWeekdayDate } from "../core/model/dates";
import { METRICS } from "../core/model/palette";
import { buildView } from "../core/model/screen";
import CompareWith from "../ui/CompareWith";
import DayDetail from "../ui/DayDetail";
import DetailSection from "../ui/DetailSection";
import KpiStrip from "../ui/KpiStrip";
import MetricTabs from "../ui/MetricTabs";
import SummaryCards from "../ui/SummaryCards";
import WellnessCalendar from "../ui/WellnessCalendar";
import WellnessTrend from "../ui/WellnessTrend";

/** The slide-in for a tab or a Compare-with switch; none when nothing was picked. */
const slideClass = (from) =>
  from === "right" ? "motion-safe:animate-tab-in-right" : from === "left" ? "motion-safe:animate-tab-in-left" : "";

const errorMessage = (error) =>
  error?.response?.data?.detail || error?.response?.data?.message || error?.message || "Something went wrong.";

const Spinner = () => (
  <div className="flex justify-center items-center py-24 w-full">
    <div className="w-12 h-12 border-4 border-dashed rounded-full animate-spin border-orange-primary" />
  </div>
);

/**
 * Hormonal Wellness — a read-only view of the client's Period, PMS, Energy and
 * Cravings logs from the Flex app. Recreates `Hormonal Wellness 1a.dc.html`;
 * data rules follow `.md/HORMONE_TRACKER_README.md` (see the feature README).
 *
 * @param {Object} props
 * @param {string} props.profileId - The client's UserProfile id.
 */
const HormonalWellnessTab = ({ profileId }) => {
  const { data, today, loading, error, refresh } = useHormoneData(profileId);
  const [tab, setTab] = useState("periods");
  // Which side the new tab slides in from: the side of the tab picked.
  const [slideFrom, setSlideFrom] = useState("right");
  // Per metric: calendar month, selected day; per section: compare range, open.
  const [months, setMonths] = useState({});
  const [selected, setSelected] = useState({});
  const [ranges, setRanges] = useState({});
  // Per section: which side its chart slides in from after a Compare-with switch.
  const [rangeSlide, setRangeSlide] = useState({});
  const [open, setOpen] = useState({});

  const ctx = useMemo(() => (data ? buildContext(data) : null), [data]);
  const cards = useMemo(() => (ctx?.hasData ? buildCards(ctx) : []), [ctx]);
  const view = useMemo(
    () => (ctx?.hasData ? buildView(ctx, { tab, ranges, open, month: months[tab], selected: selected[tab] }) : null),
    [ctx, tab, ranges, open, months, selected]
  );

  const shell = (children) => (
    <div className="w-full min-w-0 font-dmsans antialiased text-[#1f1f1f] bg-[#FAF9F8] rounded-[14px] px-[28px] pt-[24px] pb-[36px] flex flex-col gap-[18px]">
      <div className="flex flex-col gap-[2px]">
        <span className="text-[20px] font-bold">Hormonal Wellness</span>
        <span className="text-[13px] text-[#6b6b6b]">Today · {shortWeekdayDate(today)}</span>
      </div>
      {children}
    </div>
  );

  if (loading) return shell(<Spinner />);
  if (error && !data) {
    return shell(
      <EmptyState
        icon={<FiAlertCircle className="w-12 h-12 text-gray-400 mx-auto" />}
        title="Couldn’t load hormone data"
        hint={errorMessage(error)}
        action={
          <button type="button" onClick={refresh} className="px-4 py-2 rounded-lg bg-[#1f1f1f] text-white text-sm font-semibold">
            Try again
          </button>
        }
      />
    );
  }
  if (!view) {
    return shell(
      <EmptyState
        icon={<FiActivity className="w-12 h-12 text-gray-400 mx-auto" />}
        title="No period logged yet"
        hint="Cycle, PMS, energy and craving insights appear once the client logs a period in the Flex app."
      />
    );
  }

  const pickTab = (id) => {
    if (id === tab) return;
    const order = (m) => METRICS.findIndex((x) => x.id === m);
    setSlideFrom(order(id) > order(tab) ? "right" : "left");
    // The whole tab slides in; its charts must not slide again inside it.
    setRangeSlide({});
    setTab(id);
  };
  const setRange = (sectionId, key) => {
    const k = `${tab}-${sectionId}`;
    const was = ranges[k] || "prev";
    if (key === was) return;
    setRangeSlide((s) => ({ ...s, [k]: RANGE_KEYS.indexOf(key) > RANGE_KEYS.indexOf(was) ? "right" : "left" }));
    setRanges((s) => ({ ...s, [k]: key }));
  };
  // Keyed by the active range, so a Compare-with switch replays the slide-in.
  const rangeKey = (tf) => tf.opts.find((o) => o.active)?.key;
  const rangeSlideClass = (tf) => slideClass(rangeSlide[`${tab}-${tf.sectionId}`]);
  const stepMonth = (delta) => setMonths((s) => ({ ...s, [tab]: view.monthIndex + delta }));

  return shell(
    <>
      <SummaryCards cards={cards} active={tab} onPick={pickTab} />
      <MetricTabs active={tab} onPick={pickTab} />
      {/* Keyed by tab so each switch replays the slide-in; the animation fills
          backwards only, so no transform is left on the content afterwards. */}
      <div key={tab} className={`flex flex-col gap-[16px] ${slideClass(slideFrom)}`}>
        <KpiStrip primary={view.primary} secondary={view.secondary} lists={view.lists} />

        <div className="bg-white border border-[#ECECEC] rounded-[12px] grid grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] overflow-hidden">
          <div className="px-[22px] py-[20px]">
            <WellnessCalendar
              cal={view.cal}
              onPrev={() => stepMonth(-1)}
              onNext={() => stepMonth(1)}
              onSelect={(iso) => setSelected((s) => ({ ...s, [tab]: iso }))}
            />
          </div>
          <div className="border-l border-[#F1ECE8]">
            <DayDetail detail={view.detail} />
          </div>
        </div>

        <div className="min-w-0 bg-white border border-[#ECECEC] rounded-[12px] px-[22px] py-[20px] flex flex-col gap-[14px]">
          <CompareWith tf={view.trendTf} onPick={setRange} />
          <div key={rangeKey(view.trendTf)} className={rangeSlideClass(view.trendTf)}>
            <WellnessTrend trend={view.trend} size="md" />
          </div>
        </div>

        {view.details.map((d) => (
          <DetailSection
            key={d.id}
            d={d}
            bodyKey={rangeKey(d.tf)}
            bodyClass={rangeSlideClass(d.tf)}
            onRange={setRange}
            onToggle={() => setOpen((s) => ({ ...s, [`${tab}-${d.id}`]: !d.isOpen }))}
          />
        ))}
      </div>
    </>
  );
};

export default HormonalWellnessTab;
